import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { db } from "@/firebase/config";
import { collection, getDocs, query } from "firebase/firestore";
import StudentHeader from "./StudentHeader";
import StudentSidebar from "./StudentSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { 
  Gamepad2, 
  Target, 
  Zap, 
  Trophy,
  Star,
  Rocket,
  Sparkles,
  Gift,
  Crown,
  Flame
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from "recharts";
import ContentLoader from "../authentication/ContentLoader";
import EnrollmentRequiredBanner from "../components/EnrollmentRequiredBanner";

const gamificationStats = [
  {
    icon: Gamepad2,
    title: "Learning Points",
    value: "2,847",
    change: "+127 today",
    color: "bg-blue-500"
  },
  {
    icon: Target,
    title: "Streak Days",
    value: "23",
    change: "Keep it up!",
    color: "bg-green-500"
  },
  {
    icon: Zap,
    title: "Energy Level",
    value: "Super!",
    change: "Ready to learn",
    color: "bg-yellow-500"
  },
  {
    icon: Trophy,
    title: "Badges Earned",
    value: "12",
    change: "3 new this week",
    color: "bg-purple-500"
  }
];

const skillsRadarData = [
  { skill: "Reading", level: 85, fullMark: 100 },
  { skill: "Math", level: 78, fullMark: 100 },
  { skill: "Science", level: 92, fullMark: 100 },
  { skill: "Art", level: 88, fullMark: 100 },
  { skill: "Sports", level: 75, fullMark: 100 },
  { skill: "Music", level: 82, fullMark: 100 },
];

const weeklyProgressData = [
  { day: "Mon", points: 120, challenges: 3 },
  { day: "Tue", points: 145, challenges: 4 },
  { day: "Wed", points: 180, challenges: 5 },
  { day: "Thu", points: 165, challenges: 4 },
  { day: "Fri", points: 200, challenges: 6 },
];

const chartConfig = {
  points: { label: "Learning Points", color: "#3B82F6" },
  challenges: { label: "Challenges", color: "#10B981" },
  level: { label: "Skill Level", color: "#8B5CF6" }
};

const StudentDashboard = () => {
  const user = useSelector((state: any) => state.users.currentUser || {});
  const { id: userId, selectedAffiliation } = user;
  const school = selectedAffiliation || {};
  const [dorms, setDorms] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log("Current user state in StudentDashboard:", user);
    if (!userId) {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] No userId found, skipping dorm fetch`);
      setIsLoading(false);
      return;
    }

    const fetchDorms = async () => {
      if (!school?.schoolID) {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Student has no schoolID assigned:`, userId);
        setIsLoading(false);
        return;
      }

      try {
        const dormsQuery = query(collection(db, `schools/${school.schoolID}/dorms`));
        const dormsSnapshot = await getDocs(dormsQuery);
        const dormsList = dormsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (dormsList.length > 0) {
          console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Student access verified - Dorms fetched for user ${userId} (schoolID: ${school.schoolID}):`, dormsList);
        } else {
          console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] No dorms found for schoolID: ${school.schoolID} for user ${userId}`);
        }
        setDorms(dormsList);
      } catch (error) {
        console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error fetching dorms for user ${userId}:`, error);
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Student access denied or data unavailable for user ${userId}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDorms();
  }, [userId, school?.schoolID]);

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <div className="h-full border-r border-gray-200 dark:border-gray-700">
          <StudentSidebar />
        </div>
        <div className="flex flex-col flex-1 h-full dark:border-[#364153]">
          <StudentHeader />
          <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
            <ContentLoader isLoading={isLoading} message="">
              {!school?.schoolID ? (
                <EnrollmentRequiredBanner role="student" />
              ) : (
                <>
                  <div className="mb-4 sm:mb-6">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 flex items-center gap-2">
                      <Rocket className="h-6 w-6 text-blue-500" />
                      My Learning Adventure
                    </h1>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                      Level up your skills and unlock amazing achievements!
                    </p>
                  </div>
                  
                  {/* Gamification Stats */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
                    {gamificationStats.map((stat, index) => (
                      <Card key={index} className="hover:shadow-lg transition-all duration-300 hover:scale-105 dark:bg-gray-800 dark:border-gray-700 border-2">
                        <CardHeader className="pb-2 p-3 sm:p-6">
                          <div className="flex items-center justify-between">
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 ${stat.color} rounded-lg flex items-center justify-center shadow-lg`}>
                              <stat.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                            <Sparkles className="h-4 w-4 text-yellow-500" />
                          </div>
                          <CardTitle className="text-base sm:text-lg dark:text-white">{stat.title}</CardTitle>
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                          <div className="text-xs text-green-600 font-medium">{stat.change}</div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>

                  {/* Skills and Progress */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
                    <Card className="dark:bg-gray-800 dark:border-gray-700">
                      <CardHeader className="p-3 sm:p-6">
                        <CardTitle className="flex items-center gap-2 dark:text-white">
                          <Star className="h-5 w-5 text-yellow-500" />
                          My Super Skills
                        </CardTitle>
                        <CardDescription className="dark:text-gray-300">See how awesome you are at different subjects!</CardDescription>
                      </CardHeader>
                      <CardContent className="p-3 sm:p-6 pt-0">
                        <ChartContainer config={chartConfig} className="h-[250px] w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={skillsRadarData}>
                              <PolarGrid />
                              <PolarAngleAxis dataKey="skill" tick={{ fontSize: 10 }} />
                              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} />
                              <Radar
                                name="Skills"
                                dataKey="level"
                                stroke="#8B5CF6"
                                fill="#8B5CF6"
                                fillOpacity={0.3}
                                strokeWidth={2}
                              />
                            </RadarChart>
                          </ResponsiveContainer>
                        </ChartContainer>
                      </CardContent>
                    </Card>

                    <Card className="dark:bg-gray-800 dark:border-gray-700">
                      <CardHeader className="p-3 sm:p-6">
                        <CardTitle className="flex items-center gap-2 dark:text-white">
                          <Flame className="h-5 w-5 text-orange-500" />
                          This Week's Power-Up
                        </CardTitle>
                        <CardDescription className="dark:text-gray-300">Your learning points and challenges completed</CardDescription>
                      </CardHeader>
                      <CardContent className="p-3 sm:p-6 pt-0">
                        <ChartContainer config={chartConfig} className="h-[250px] w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={weeklyProgressData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="day" fontSize={10} />
                              <YAxis fontSize={10} />
                              <ChartTooltip content={<ChartTooltipContent />} />
                              <Bar dataKey="points" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </ChartContainer>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Achievement Showcase */}
                  <Card className="dark:bg-gray-800 dark:border-gray-700">
                    <CardHeader className="p-3 sm:p-6">
                      <CardTitle className="flex items-center gap-2 dark:text-white">
                        <Crown className="h-5 w-5 text-yellow-500" />
                        My Achievement Collection
                      </CardTitle>
                      <CardDescription className="dark:text-gray-300">Look at all the awesome badges you've earned!</CardDescription>
                    </CardHeader>
                    <CardContent className="p-3 sm:p-6 pt-0">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="flex flex-col items-center p-4 bg-gradient-to-br from-yellow-100 to-yellow-200 dark:from-yellow-800 dark:to-yellow-900 rounded-lg">
                          <Trophy className="h-8 w-8 text-yellow-600 mb-2" />
                          <span className="text-xs font-medium text-yellow-800 dark:text-yellow-200 text-center">Math Master</span>
                        </div>
                        <div className="flex flex-col items-center p-4 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-800 dark:to-blue-900 rounded-lg">
                          <Star className="h-8 w-8 text-blue-600 mb-2" />
                          <span className="text-xs font-medium text-blue-800 dark:text-blue-200 text-center">Reading Hero</span>
                        </div>
                        <div className="flex flex-col items-center p-4 bg-gradient-to-br from-green-100 to-green-200 dark:from-green-800 dark:to-green-900 rounded-lg">
                          <Rocket className="h-8 w-8 text-green-600 mb-2" />
                          <span className="text-xs font-medium text-green-800 dark:text-green-200 text-center">Science Explorer</span>
                        </div>
                        <div className="flex flex-col items-center p-4 bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-800 dark:to-purple-900 rounded-lg">
                          <Gift className="h-8 w-8 text-purple-600 mb-2" />
                          <span className="text-xs font-medium text-purple-800 dark:text-purple-200 text-center">Helper Friend</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Dorms Section */}
                  <div className="mt-6">
                    <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Dorms</h2>
                    {dorms.length > 0 ? (
                      <ul className="list-disc pl-5">
                        {dorms.map((dorm) => (
                          <li key={dorm.id} className="mb-2 text-gray-700 dark:text-gray-300">
                            {dorm.dormName} (Capacity: {dorm.capacity}, Available: {dorm.available}, Patron: {dorm.patronTeacherId || "None"})
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-600 dark:text-gray-300">No dorms available for this school.</p>
                    )}
                  </div>
                </>
              )}
            </ContentLoader>
            <Outlet />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default StudentDashboard;